/* global SWDLocalise */
import { gsap } from 'gsap';
import { getClosest, handleResponse } from '../../utils';
import initCompanyNumberInputs, { resetCompanyNumberStatuses } from './companies-house';
import { initFieldGroups, resetFieldGroups } from './field-group';
import { initFormDummyData } from './dummy-data';
import { initSearchSelects, resetSearchSelects } from './search-select';
import { initSelectFieldTargets, syncSelectFieldTargets } from './select-targets';

let forms;

const getForms = () => document.querySelectorAll('.js-form');

const resetForm = (index) => {
    forms[index].inputs.forEach((input) => {
        switch (input.type) {
            case 'text':
            case 'textarea':
            case 'email':
            case 'select-one':
                input.value = '';
                break;

            case 'checkbox':
                input.checked = false;
                break;

            // no default
        }
    });

    forms[index].radioInputs.forEach((input) => {
        input.checked = false;
    });

    forms[index].checkboxInputs.forEach((input) => {
        input.checked = false;
    });

    forms[index].checkboxOtherInputs.forEach((input) => {
        input.value = '';
    });

    resetFieldGroups(forms[index]);
    resetSearchSelects(forms[index]);
    resetCompanyNumberStatuses(forms[index]);
    syncSelectFieldTargets(forms[index]);

    forms[index].inputs = forms[index].querySelectorAll('.js-form-input');
};

const disableForm = (index) => {
    gsap.to(forms[index].formInner, {
        duration: 0.5,
        pointerEvents: 'none',
    });
    forms[index].loader.style.display = 'flex';
};

const enableForm = (index) => {
    gsap.to(forms[index].formInner, {
        duration: 0.5,
        pointerEvents: 'auto',
    });
    forms[index].loader.style.display = 'none';
};

const getFieldBaseKey = (input) => input.dataset.baseKey
    || input.dataset.key?.replace(/__\d+$/, '')
    || '';

const getFieldValue = (input) => {
    // A checkbox's .value is always its value attribute regardless of state, so
    // read the checked state — an unticked box submits an empty value.
    if (input.type === 'checkbox') {
        return input.checked ? input.value : '';
    }

    return input.value;
};

const collectFieldEntry = (input) => ({
    name: input.getAttribute('name'),
    value: getFieldValue(input),
    required: input.classList.contains('is-required'),
    label: input.dataset.label || '',
    key: input.dataset.key || '',
    baseKey: getFieldBaseKey(input),
});

const collectFormPayload = (form) => {
    const fields = [];
    const fieldGroups = [];

    form.querySelectorAll('.js-form-field-group').forEach((group) => {
        const batches = [];

        group.querySelectorAll('.js-form-field-group-batch').forEach((batch) => {
            const batchFields = [];

            batch.querySelectorAll('.js-form-input').forEach((input) => {
                if (getClosest(input, '.js-form-select-target.hidden')) return;

                batchFields.push(collectFieldEntry(input));
            });

            batches.push({
                batchIndex: Number(batch.dataset.batchIndex || 0),
                fields: batchFields,
            });
        });

        fieldGroups.push({
            groupIndex: Number(group.dataset.groupIndex || 0),
            batches,
        });
    });

    form.querySelectorAll('.js-form-input').forEach((input) => {
        if (input.closest('.js-form-field-group')) return;
        if (getClosest(input, '.js-form-select-target.hidden')) return;

        fields.push(collectFieldEntry(input));
    });

    return {
        version: 2,
        fields,
        fieldGroups,
    };
};

const notice = (index, message, reset, type = 'error') => {
    let formatMessage = message;

    if (!Array.isArray(formatMessage)) {
        formatMessage = [formatMessage];
    }

    formatMessage = formatMessage.filter((item, idx) => formatMessage.indexOf(item) === idx);

    forms[index].notice.innerHTML = `
        <ul class="p-4 mt-4 mb-0 text-white rounded-lg list-none ${type === 'error' ? 'bg-error' : 'bg-success'}">${formatMessage.map((line) => `<li class="mt-0.5 first:mt-0 text-xs font-medium">${line}</li>`).join('')}</ul>
    `;

    if (reset) resetForm(index);

    enableForm(index);
};

const clickHandle = (event, index, form) => {
    event.preventDefault();

    disableForm(index);

    const errors = [];
    let send = true;
    let email = '';
    const inputs = form.querySelectorAll('.js-form-input');

    inputs.forEach((input) => {
        // inputs
        input.parentNode.classList.remove('has-error');

        // radio inputs
        if (input.classList.contains('js-form-radio-input')) {
            const radioInputs = input.parentNode.querySelectorAll('.js-form-radio');
            radioInputs.forEach((radioInput) => {
                radioInput.classList.remove('has-error');
            });
        }

        // checkbox inputs
        if (input.classList.contains('js-form-checkbox-input')) {
            const checkboxInputs = input.parentNode.querySelectorAll('.js-form-checkbox');
            const values = [];
            checkboxInputs.forEach((checkboxInput) => {
                checkboxInput.parentNode.classList.remove('has-error');

                switch (checkboxInput.type) {
                    case 'text':
                        if (checkboxInput.value.length > 0) {
                            values.push(checkboxInput.value);
                        }
                        break;
                    case 'checkbox':
                        if (checkboxInput.checked) {
                            values.push(checkboxInput.value);
                        }
                        break;
                    // no default
                }
            });

            input.value = values.join(' || ');
        }

        // consent
        if (forms[index].formConsent) {
            forms[index].formConsent.parentNode.classList.remove('has-error');
        }

        // ignore if field is hidden
        const isHidden = getClosest(input, '.js-form-select-target.hidden');
        if (isHidden) return;

        const required = input.classList.contains('is-required');
        if (required) {
            switch (input.type) {
                case 'text':
                case 'textarea':
                case 'email':
                case 'hidden':
                case 'select-one':
                    if (!input.value || input.value === 0) {
                        send = false;

                        if (input.parentNode.classList.contains('group/input')) {
                            input.parentNode.classList.add('has-error');
                        } else if (input.classList.contains('js-form-radio-input')) {
                            const radioInputs = input.parentNode.querySelectorAll('.js-form-radio');
                            radioInputs.forEach((radioInput) => {
                                radioInput.classList.add('has-error');
                            });
                        } else if (input.classList.contains('js-form-checkbox-input')) {
                            const checkboxInputs = input.parentNode.querySelectorAll('.js-form-checkbox');
                            checkboxInputs.forEach((checkboxInput) => {
                                checkboxInput.parentNode.classList.add('has-error');
                            });
                        } else {
                            input.parentNode.classList.add('has-error');
                        }
                    }
                    break;

                case 'checkbox':
                    if (input.checked === false) {
                        send = false;
                        input.parentNode.classList.add('has-error');
                    }
                    break;

                // no default
            }

            if (!send) {
                errors.push('Please complete the fields marked in red');
            }
        }

        // get primary email address
        if (input.type === 'email'
            && input.classList.contains('js-form-email')) {
            email = input.value;

            // check email is confirmed
            const emailConfirm = form.querySelector('.js-form-email-confirm');
            if (emailConfirm && email !== emailConfirm.value) {
                send = false;
                input.parentNode.classList.add('has-error');
                errors.push('Please check your email addresses match');
            }
        }
    });

    // data consent
    if (forms[index].formConsent && !forms[index].formConsent.checked) {
        send = false;
        forms[index].formConsent.parentNode.classList.add('has-error');
        errors.push('Please tick the consent checkbox');
    }

    if (!email) {
        send = false;
        errors.push('A primary email address is required');
    }

    if (!send) {
        notice(index, errors);
    } else {
        const data = new FormData();
        data.append('action', form.dataset.action);
        data.append('nonce', form.dataset.nonce);
        data.append('email', email);
        data.append('form_data', JSON.stringify(collectFormPayload(form)));

        fetch(SWDLocalise.ajaxUrl, {
            method: 'POST',
            cache: 'no-cache',
            body: data,
        })
            .then(handleResponse)
            .then((response) => {
                if (response.success) {
                    notice(index, (response.message.length > 0 ? response.message : form.dataset.successMessage), true, 'success');
                } else {
                    notice(index, response.message);
                }
            })
            .catch((error) => {
                console.error(error); // eslint-disable-line no-console
            });
    }
};

const radioClickHandle = (input) => {
    if (input.checked) {
        const hiddenInput = input.parentNode.parentNode.querySelector('input[type=hidden]');
        hiddenInput.value = input.value;
    }
};

const init = () => {
    forms = getForms();
    if (!forms) return;

    forms.forEach((form, i) => {
        forms[i].btn = form.querySelector('.js-form-btn');
        if (!forms[i].btn) return;

        forms[i].formInner = form.querySelector('.js-form-inner');
        forms[i].notice = form.querySelector('.js-form-notice');
        forms[i].formConsent = form.querySelector('.js-form-consent');
        forms[i].inputs = form.querySelectorAll('.js-form-input');
        forms[i].radioInputs = form.querySelectorAll('.js-form-radio');
        forms[i].checkboxInputs = form.querySelectorAll('.js-form-checkbox');
        forms[i].checkboxOtherInputs = form.querySelectorAll('.js-form-checkbox-other');
        forms[i].loader = form.querySelector('.js-form-loader');
        forms[i].selectInputs = form.querySelectorAll('.js-form-select');

        // prevent multiple events firing
        const newClickHandle = (event) => {
            clickHandle(event, i, form);
        };
        form.btn.addEventListener('click', newClickHandle, false);

        // radio inputs
        if (forms[i].radioInputs.length > 0) {
            forms[i].radioInputs.forEach((input) => {
                // prevent multiple events firing
                const newRadioClickHandle = () => {
                    radioClickHandle(input);
                };

                input.addEventListener('click', newRadioClickHandle, false);
            });
        }

        // select form fields
        initSelectFieldTargets(form);

        // field groups
        initFieldGroups(form);

        // company number inputs
        initCompanyNumberInputs(form);

        // search selects
        initSearchSelects(form);

        // dummy data (non-production only)
        initFormDummyData(form);
    });
};

export default init;
